import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY } from "../lib/data";

const Gallery = () => {
  const [active, setActive] = useState(null);

  const next = () => setActive((i) => (i + 1) % GALLERY.length);
  const prev = () => setActive((i) => (i - 1 + GALLERY.length) % GALLERY.length);

  return (
    <section
      id="gallery"
      className="relative py-28 md:py-40 bg-pw overflow-hidden"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="gold-line" />
            <span className="overline">Gallery</span>
            <span className="gold-line" />
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[var(--pw-text)] max-w-3xl mx-auto">
            Frammenti di <em className="text-[var(--pw-gold)]">cerimonie</em>,
            note che restano.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[200px] md:auto-rows-[260px]">
          {GALLERY.map((g, i) => (
            <motion.button
              key={i}
              onClick={() => setActive(i)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`relative overflow-hidden group ${
                i === 0 || i === 4 ? "md:row-span-2" : ""
              } ${i === 3 ? "md:col-span-2" : ""}`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                className="w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                <span className="font-serif italic text-sm text-[var(--pw-text)] translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  {g.caption}
                </span>
                <span className="text-[10px] text-[var(--pw-gold)] tracking-[0.25em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="lightbox"
          >
            <button
              onClick={(e) => { e.stopPropagation(); setActive(null); }}
              className="absolute top-6 right-6 text-white/70 hover:text-[var(--pw-gold)] z-10"
              data-testid="lightbox-close"
            >
              <X size={28} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prev(); }}
              className="absolute left-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[var(--pw-gold)] z-10"
              data-testid="lightbox-prev"
            >
              <ChevronLeft size={32} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next(); }}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-white/70 hover:text-[var(--pw-gold)] z-10"
              data-testid="lightbox-next"
            >
              <ChevronRight size={32} />
            </button>
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              src={GALLERY[active].src}
              alt={GALLERY[active].alt}
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
